import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useNavigate, useLocation, useParams } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import Layout from './components/Layout';
import Home from './pages/Home';
import Matches from './pages/Matches';
import Login from './pages/Login';
import Register from './pages/Register';
import AuthPage from './pages/AuthPage';
import Wallet from './pages/Wallet';
import Referrals from './pages/Referrals';
import PredictionRules from './pages/PredictionRules';
import Earn from './pages/Earn';
import Redeem from './pages/Redeem';
import TermsAndConditions from './pages/TermsAndConditions';
import PrivacyPolicy from './pages/PrivacyPolicy';
import Support from './pages/Support';
import Contact from './pages/Contact';
import LoginRewardToast from './components/LoginRewardToast';
import AdminMatches from './pages/AdminMatches';
import FinishSignIn from './pages/FinishSignIn';
import ForgotPassword from './pages/ForgotPassword';
import ScrollToTop from './components/ScrollToTop';
import TestCode from './pages/TestCode';
import AdminLogin from './pages/AdminLogin';

import AdminPanel from './components/AdminPanel';
import AdminRouteGuard from './components/AdminRouteGuard';

// Import new pages for admin and user home
import AdminDashboard from './pages/AdminDashboard';
import UserHome from './pages/UserHome';

// Context providers
import { AuthProvider, useAuth } from './context/AuthContext';
import { WalletProvider, useWallet } from './context/WalletContext';
import { ReferralProvider } from './context/ReferralContext';
import { PredictionProvider } from './context/PredictionContext';

// Import match types from cricketAPI
import { MATCH_TYPES } from './services/cricketAPI';
import PredictionHistory from './components/PredictionHistory';
import AdminBlogRewards from './pages/AdminBlogRewards';
import { fetchUserRole, getRedirectPathByRole } from './services/userService';

// More visually distinct placeholder components
const MatchDetailsPage = () => {
  const { matchId } = useParams();
  const [selectedPredictionType, setSelectedPredictionType] = React.useState('matchWinner');
  const [showPredictionForm, setShowPredictionForm] = React.useState(false);
  const [predictionAmount, setPredictionAmount] = React.useState(100);
  const [selectedTeam, setSelectedTeam] = React.useState('');
  const [selectedScoreRange, setSelectedScoreRange] = React.useState('');
  const [selectedOver, setSelectedOver] = React.useState('');
  const [selectedOverRuns, setSelectedOverRuns] = React.useState('');
  const [selectedWicket, setSelectedWicket] = React.useState('');
  const [firstTeamScore, setFirstTeamScore] = React.useState('');
  const [activeTab, setActiveTab] = React.useState('predictions'); // 'predictions' or 'history'
  const [loading, setLoading] = React.useState(true);
  
  // Match data state
  const [match, setMatch] = React.useState(null);
  
  // Fetch match details when component mounts or matchId changes
  React.useEffect(() => {
    const fetchMatchDetails = async () => {
      try {
        setLoading(true);
        // Import the match service to get match details
        const { getMatchDetails } = await import('./services/cricketAPI');
        
        // Fetch match details
        const matchData = await getMatchDetails(matchId || 'ipl1'); // Default to first IPL match if no ID
        
        // Update match state
        setMatch(matchData);
        
        // Reset selected over when match type changes
        setSelectedOver('');
      } catch (error) {
        console.error('Error fetching match details:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchMatchDetails();
  }, [matchId]);
  
  // Score ranges for prediction
  const scoreRanges = [
    '0-120', '121-140', '141-160', '161-180', '181-200', '201-220', '220+'
  ];
  
  // Possible first team scores - dynamically set based on match type
  const getFirstTeamScoresForMatchType = (matchType) => {
    switch(matchType) {
      case 'IPL':
      case 'T20':
        return Array.from({ length: 30 }, (_, i) => ((i + 1) * 10).toString());
      case 'ODI':
        return Array.from({ length: 45 }, (_, i) => ((i + 1) * 10).toString());
      case 'TEST':
        return Array.from({ length: 70 }, (_, i) => ((i + 1) * 10).toString());
      default:
        return Array.from({ length: 30 }, (_, i) => ((i + 1) * 10).toString());
    }
  };
  
  const firstTeamScores = match ? getFirstTeamScoresForMatchType(match.matchType) : [];
  
  // Available overs for prediction - will be dynamically set based on match type
  const getOversForMatchType = (matchType) => {
    switch(matchType) {
      case 'IPL':
      case 'T20':
        return Array.from({ length: 20 }, (_, i) => (i + 1).toString());
      case 'ODI':
        return Array.from({ length: 50 }, (_, i) => (i + 1).toString());
      case 'TEST':
        return Array.from({ length: 90 }, (_, i) => (i + 1).toString());
      default:
        return Array.from({ length: 20 }, (_, i) => (i + 1).toString());
    }
  };
  
  // Over run options - individual runs from 1 to 40
  const overRuns = Array.from({ length: 40 }, (_, i) => (i + 1).toString());
  
  // Wicket prediction options
  const wicketOptions = ['Yes', 'No'];
  
  // Prediction Multipliers - all set to 2x
  const multipliers = {
    matchWinner: 3,
    scoreRange: 3,
    overByOver: 3,
    wicketPrediction: 3,
    firstTeamScore: 3
  };
  
  const handleOpenPredictionForm = () => {
    setShowPredictionForm(true);
  };
  
  const handleClosePredictionForm = () => {
    setShowPredictionForm(false);
  };
  
  // Use the useAuth hook to get current user
  const { currentUser } = useAuth();
  
  const handleSubmitPrediction = async (e) => {
    e.preventDefault();
    
    if (!match) return;
    
    // Check if user is logged in
    if (!currentUser) {
      alert("Please log in to make predictions");
      return;
    }
    
    // Get prediction selection based on type
    const selection = selectedPredictionType === 'matchWinner' ? selectedTeam : 
                      selectedPredictionType === 'scoreRange' ? selectedScoreRange :
                      selectedPredictionType === 'wicketPrediction' ? `${selectedOver}th over: ${selectedWicket}` :
                      selectedPredictionType === 'firstTeamScore' ? firstTeamScore :
                      `${selectedOver}th over: ${selectedOverRuns} runs`;
    
    // Create prediction data object
    const predictionData = {
      type: selectedPredictionType,
      amount: predictionAmount,
      selection,
      matchId: match.id,
      matchType: match.matchType,
      multiplier: multipliers[selectedPredictionType] || 2
    };
    
    // Log the prediction for development
    console.log('Prediction submitted:', predictionData);
    
    try {
      // Import prediction service functions (in a real app, would be imported at the top)
      const { createPrediction } = await import('./services/predictionService');
      const { spendFromWalletA } = await import('./services/walletService');
      
      // 1. Deduct amount from Wallet A
      await spendFromWalletA(currentUser.uid, predictionAmount);
      
      // 2. Save prediction to Firestore and update prediction cycle
      await createPrediction(currentUser.uid, match.id, predictionData);
      
      // 3. Close the form
      handleClosePredictionForm();
      
      // 4. Refresh predictions list (would be implemented in a real app)
      // await loadUserPredictions();
      
    } catch (error) {
      console.error("Error submitting prediction:", error);
      alert("Failed to submit prediction. Please try again.");
    }
  };
  
  const renderPredictionForm = () => {
    switch(selectedPredictionType) {
      case 'matchWinner':
        return (
          <div className="mt-4">
            <label className="block text-sm font-medium mb-2">Select Team</label>
            <div className="grid grid-cols-2 gap-3">
              {match.teams.map(team => (
                <button
                  key={team}
                  type="button"
                  className={`p-3 border rounded-md text-center ${
                    selectedTeam === team 
                      ? 'bg-blue-100 border-blue-500 text-blue-600 dark:bg-blue-900 dark:border-blue-400 dark:text-blue-300' 
                      : 'border-gray-300 dark:border-gray-600'
                  }`}
                  onClick={() => setSelectedTeam(team)}
                >
                  {team}
                </button>
              ))}
            </div>
            <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
              Payout: {predictionAmount} × {multipliers.matchWinner}x = {predictionAmount * multipliers.matchWinner} coins
            </p>
          </div>
        );
        
      case 'scoreRange':
        return (
          <div className="mt-4">
            <label className="block text-sm font-medium mb-2">Select Team Score Range</label>
            <select
              className="w-full p-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600"
              value={selectedScoreRange}
              onChange={(e) => setSelectedScoreRange(e.target.value)}
            >
              <option value="">Select Range</option>
              {scoreRanges.map(range => (
                <option key={range} value={range}>{range}</option>
              ))}
            </select>
            <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
              Payout: {predictionAmount} × {multipliers.scoreRange}x = {predictionAmount * multipliers.scoreRange} coins
            </p>
          </div>
        );
        
      case 'overByOver':
        return (
          <div>
            <div className="flex flex-col space-y-4">
              <div>
                <p className="text-sm font-medium mb-2">Select Over</p>
                <div className="relative">
                  <select
                    className="w-full p-3 bg-gray-900 text-white border border-gray-700 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 appearance-none pr-10"
                    value={selectedOver}
                    onChange={(e) => setSelectedOver(e.target.value)}
                  >
                    <option value="">Select Over</option>
                    {getOversForMatchType(match.matchType).map(over => (
                      <option key={over} value={over}>Over {over}</option>
                    ))}
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                    </svg>
                  </div>
                </div>
              </div>
              
              <div>
                <p className="text-sm font-medium mb-2">Predicted Runs</p>
                <div className="relative">
                  <select
                    className="w-full p-3 bg-gray-900 text-white border border-gray-700 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 appearance-none pr-10"
                    value={selectedOverRuns}
                    onChange={(e) => setSelectedOverRuns(e.target.value)}
                  >
                    <option value="">Select Runs</option>
                    {overRuns.map(runs => (
                      <option key={runs} value={runs}>{runs} run{parseInt(runs) !== 1 ? 's' : ''}</option>
                    ))}
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                    </svg>
                  </div>
                </div>
              </div>
            </div>
            
            <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
              Payout: {predictionAmount} × {multipliers.overByOver}x = {predictionAmount * multipliers.overByOver} coins
            </p>
          </div>
        );
        
      case 'wicketPrediction':
        return (
          <div className="mt-4">
            <label className="block text-sm font-medium mb-2">Wicket Prediction</label>
            <div className="flex flex-col space-y-4">
              <div>
                <p className="text-sm font-medium mb-2">Select Over</p>
                <div className="relative">
                  <select
                    className="w-full p-3 bg-gray-900 text-white border border-gray-700 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 appearance-none pr-10"
                    value={selectedOver}
                    onChange={(e) => setSelectedOver(e.target.value)}
                  >
                    <option value="">Select Over</option>
                    {getOversForMatchType(match.matchType).map(over => (
                      <option key={over} value={over}>Over {over}</option>
                    ))}
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                    </svg>
                  </div>
                </div>
              </div>
              
              <div>
                <p className="text-sm font-medium mb-2">Will a Wicket Fall?</p>
                <div className="grid grid-cols-2 gap-3">
                  {wicketOptions.map(option => (
                    <button
                      key={option}
                      type="button"
                      className={`p-3 border rounded-md text-center ${
                        selectedWicket === option 
                          ? 'bg-blue-100 border-blue-500 text-blue-600 dark:bg-blue-900 dark:border-blue-400 dark:text-blue-300' 
                          : 'border-gray-300 dark:border-gray-600'
                      }`}
                      onClick={() => setSelectedWicket(option)}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
              Payout: {predictionAmount} × {multipliers.wicketPrediction}x = {predictionAmount * multipliers.wicketPrediction} coins
            </p>
          </div>
        );
        
      case 'firstTeamScore':
        return (
          <div className="mt-4">
            <label className="block text-sm font-medium mb-2">Predict First Team's Score</label>
            <div className="relative">
              <select
                className="w-full p-3 bg-gray-900 text-white border border-gray-700 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 appearance-none pr-10"
                value={firstTeamScore}
                onChange={(e) => setFirstTeamScore(e.target.value)}
              >
                <option value="">Select Score</option>
                {firstTeamScores.map(score => (
                  <option key={score} value={score}>{score}</option>
                ))}
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                </svg>
              </div>
            </div>
            <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
              Payout: {predictionAmount} × {multipliers.firstTeamScore}x = {predictionAmount * multipliers.firstTeamScore} coins
            </p>
          </div>
        );
        
      default:
        return null;
    }
  };
  
  // Time remaining until match starts or for prediction lock
  const timeRemaining = '10:45';
  
  // If loading or no match data
  if (loading || !match) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }
  
  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Match Header */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden mb-8">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <span className="px-3 py-1 bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 rounded-full text-sm font-medium">
                {match.status || 'Upcoming'}
              </span>
              <div className="flex items-center space-x-2">
                {match.matchType && MATCH_TYPES[match.matchType] && (
                  <img
                    src={MATCH_TYPES[match.matchType].logo}
                    alt={MATCH_TYPES[match.matchType].name}
                    className="h-5 w-auto"
                  />
                )}
                <span className="text-gray-500 dark:text-gray-400 text-sm">
                  {match.matchType === 'IPL' ? 'Indian Premier League' : 
                   match.matchType === 'ODI' ? 'ODI' : 
                   match.matchType === 'TEST' ? 'Test' : match.matchType}
                </span>
              </div>
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">
              {match.venue}
            </div>
          </div>

          <div className="flex items-center justify-between mb-6">
            <div className="flex-1 text-center">
              <div className="text-2xl font-bold text-gray-900 dark:text-white mb-2">{match.teams[0]}</div>
              <div className="text-3xl font-bold text-blue-600 dark:text-blue-400">
                {match.score?.team1 || 'Yet to bat'}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                {match.scorecard?.team1?.overs || '0.0'} overs
              </div>
            </div>
            
            <div className="mx-4 text-gray-400 dark:text-gray-600">VS</div>
            
            <div className="flex-1 text-center">
              <div className="text-2xl font-bold text-gray-900 dark:text-white mb-2">{match.teams[1]}</div>
              <div className="text-3xl font-bold text-blue-600 dark:text-blue-400">
                {match.score?.team2 || 'Yet to bat'}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                {match.scorecard?.team2?.overs || '0.0'} overs
              </div>
            </div>
          </div>

          <div className="flex justify-center space-x-4">
            <button
              onClick={() => setActiveTab('predictions')}
              className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                activeTab === 'predictions'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              Make Predictions
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                activeTab === 'history'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              Prediction History
            </button>
          </div>
        </div>
      </div>

      {activeTab === 'predictions' ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Prediction Types */}
          <div className="lg:col-span-2">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
              <h2 className="text-xl font-bold mb-6">Select Prediction Type</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <button
                  onClick={() => {
                    setSelectedPredictionType('matchWinner');
                    setShowPredictionForm(true);
                  }}
                  className="p-4 border rounded-xl hover:border-blue-500 dark:hover:border-blue-400 transition-colors"
                >
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                      <svg className="w-6 h-6 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div className="text-left">
                      <h3 className="font-medium">Match Winner</h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">3× multiplier</p>
                    </div>
                  </div>
                </button>

                <button
                  onClick={() => {
                    setSelectedPredictionType('wicketPrediction');
                    setShowPredictionForm(true);
                  }}
                  className="p-4 border rounded-xl hover:border-blue-500 dark:hover:border-blue-400 transition-colors"
                >
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
                      <svg className="w-6 h-6 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                      </svg>
                    </div>
                    <div className="text-left">
                      <h3 className="font-medium">Wicket Prediction</h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">3× multiplier</p>
                    </div>
                  </div>
                </button>

                <button
                  onClick={() => {
                    setSelectedPredictionType('overByOver');
                    setShowPredictionForm(true);
                  }}
                  className="p-4 border rounded-xl hover:border-blue-500 dark:hover:border-blue-400 transition-colors"
                >
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-amber-100 dark:bg-amber-900 rounded-lg">
                      <svg className="w-6 h-6 text-amber-600 dark:text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div className="text-left">
                      <h3 className="font-medium">Over-by-Over</h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">3× multiplier</p>
                    </div>
                  </div>
                </button>

                <button
                  onClick={() => {
                    setSelectedPredictionType('firstTeamScore');
                    setShowPredictionForm(true);
                  }}
                  className="p-4 border rounded-xl hover:border-blue-500 dark:hover:border-blue-400 transition-colors"
                >
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-pink-100 dark:bg-pink-900 rounded-lg">
                      <svg className="w-6 h-6 text-pink-600 dark:text-pink-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div className="text-left">
                      <h3 className="font-medium">First Team's Score</h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">3× multiplier</p>
                    </div>
                  </div>
                </button>
              </div>
            </div>
          </div>

          {/* Match Stats */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-bold mb-6">Match Stats</h2>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-gray-600 dark:text-gray-400">Match Type</span>
                <span className="font-medium">
                  {match.matchType === 'IPL' ? 'Indian Premier League' : 
                   match.matchType === 'ODI' ? 'One Day International' : 
                   match.matchType === 'TEST' ? 'Test Match' : match.matchType}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600 dark:text-gray-400">Venue</span>
                <span className="font-medium">{match.venue}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600 dark:text-gray-400">Status</span>
                <span className="font-medium text-green-600 dark:text-green-400">{match.status || 'Upcoming'}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600 dark:text-gray-400">Start Time</span>
                <span className="font-medium">
                  {match.startTime ? new Date(match.startTime).toLocaleString() : 'To be announced'}
                </span>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden">
          <div className="p-6">
            <h2 className="text-xl font-bold mb-6">Your Predictions</h2>
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Prediction</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Amount</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  <tr className="bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-4 py-3 font-medium">IND vs AUS</td>
                    <td className="px-4 py-3 text-gray-500">Jun 15, 2023</td>
                    <td className="px-4 py-3">Match Winner</td>
                    <td className="px-4 py-3">IND</td>
                    <td className="px-4 py-3">200 coins</td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                        Won
                      </span>
                    </td>
                  </tr>
                  <tr className="bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-4 py-3 font-medium">ENG vs NZ</td>
                    <td className="px-4 py-3 text-gray-500">Jun 12, 2023</td>
                    <td className="px-4 py-3">Score Range</td>
                    <td className="px-4 py-3">140-160</td>
                    <td className="px-4 py-3">100 coins</td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
                        Lost
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Prediction Modal */}
      {showPredictionForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl max-w-md w-full p-6 relative">
            <button
              onClick={handleClosePredictionForm}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
            
            <h2 className="text-xl font-bold mb-6">Make Prediction</h2>
            
            <form onSubmit={handleSubmitPrediction}>
              {/* Amount Selection */}
              <div className="mb-6">
                <label className="block text-sm font-medium mb-3">Prediction Amount</label>
                <div className="grid grid-cols-4 gap-3">
                  {[100, 200, 500, 1000].map(amount => (
                    <button
                      key={amount}
                      type="button"
                      className={`p-3 border rounded-lg text-center font-medium transition-colors ${
                        predictionAmount === amount 
                          ? 'bg-blue-100 border-blue-500 text-blue-600 dark:bg-blue-900 dark:border-blue-400 dark:text-blue-300' 
                          : 'border-gray-300 dark:border-gray-600 hover:border-blue-500 dark:hover:border-blue-400'
                      }`}
                      onClick={() => setPredictionAmount(amount)}
                    >
                      {amount}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Prediction-specific form */}
              {renderPredictionForm()}
              
              {/* Submit Button */}
              <div className="mt-6">
                <button
                  type="submit"
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 px-4 rounded-lg font-medium transition-colors"
                  disabled={
                    (selectedPredictionType === 'matchWinner' && !selectedTeam) ||
                    (selectedPredictionType === 'scoreRange' && !selectedScoreRange) ||
                    (selectedPredictionType === 'overByOver' && (!selectedOver || !selectedOverRuns)) ||
                    (selectedPredictionType === 'wicketPrediction' && (!selectedOver || !selectedWicket)) ||
                    (selectedPredictionType === 'firstTeamScore' && !firstTeamScore)
                  }
                >
                  Submit Prediction
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

const ProfilePage = () => {
  const { currentUser } = useAuth();
  const { wallets } = useWallet();
  const walletA = wallets?.walletA || 0;
  const walletB = wallets?.walletB || 0;
  const navigate = useNavigate();
  
  return (
    <div className="max-w-5xl mx-auto">
      {/* Profile Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-700 rounded-lg shadow-lg p-6 mb-6">
        <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
          <div className="h-24 w-24 rounded-full bg-white text-blue-600 flex items-center justify-center text-4xl font-bold shadow-md">
            {currentUser?.displayName?.charAt(0) || 'U'}
          </div>
          
          <div className="flex-1 text-center md:text-left">
            <h1 className="text-2xl font-bold text-white mb-1">{currentUser?.displayName || 'User'}</h1>
            <p className="text-blue-100 mb-3">{currentUser?.email || 'user@example.com'}</p>
          </div>
          
          <div>
            <button className="bg-white text-blue-600 hover:bg-blue-50 px-4 py-2 rounded-md text-sm font-medium transition-colors">
              Edit Profile
            </button>
          </div>
        </div>
      </div>
      
      {/* Wallet Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
        {/* Wallet A */}
        <div className="bg-gradient-to-br from-purple-500 to-indigo-600 rounded-lg shadow-md p-5 text-white relative overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 -mt-8 -mr-8 bg-white/10 rounded-full"></div>
          <div className="absolute bottom-0 left-0 w-24 h-24 -mb-8 -ml-8 bg-white/10 rounded-full"></div>
          
          <h3 className="text-lg font-semibold mb-1">Wallet A</h3>
          <p className="text-sm text-purple-100 mb-3">For game entries and predictions</p>
          
          <div className="text-3xl font-bold mb-4">{walletA.toLocaleString()} coins</div>
          
          <div className="flex gap-2">
            <button 
              onClick={() => navigate('/earn')} 
              className="bg-white/20 hover:bg-white/30 text-white px-3 py-1.5 rounded text-sm transition-colors">
              Add Coins
            </button>
          </div>
        </div>
        
        {/* Wallet B */}
        <div className="bg-gradient-to-br from-amber-500 to-orange-600 rounded-lg shadow-md p-5 text-white relative overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 -mt-8 -mr-8 bg-white/10 rounded-full"></div>
          <div className="absolute bottom-0 left-0 w-24 h-24 -mb-8 -ml-8 bg-white/10 rounded-full"></div>
          
          <h3 className="text-lg font-semibold mb-1">Wallet B</h3>
          <p className="text-sm text-amber-100 mb-3">Winnings and withdrawals</p>
          
          <div className="text-3xl font-bold mb-4">₹{(walletB / 100).toFixed(2)}</div>
          
          <div className="flex gap-2">
            <button 
              onClick={() => navigate('/wallet')} 
              className="bg-white/20 hover:bg-white/30 text-white px-3 py-1.5 rounded text-sm transition-colors">
              Withdraw to Bank
            </button>
          </div>
        </div>
      </div>
      
      {/* Prediction History 
      {<div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-5">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-800 dark:text-white">Prediction History</h2>
          <div className="flex gap-2">
            <select className="text-sm border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200">
              <option>All Predictions</option>
              <option>Won</option>
              <option>Lost</option>
              <option>Pending</option>
            </select>
          </div>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-300">
              <tr>
                <th scope="col" className="px-4 py-3 rounded-tl-lg">Match</th>
                <th scope="col" className="px-4 py-3">Date</th>
                <th scope="col" className="px-4 py-3">Type</th>
                <th scope="col" className="px-4 py-3">Prediction</th>
                <th scope="col" className="px-4 py-3">Amount</th>
                <th scope="col" className="px-4 py-3 rounded-tr-lg">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              <tr className="bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700">
                <td className="px-4 py-3 font-medium">IND vs AUS</td>
                <td className="px-4 py-3 text-gray-500">Jun 15, 2023</td>
                <td className="px-4 py-3">Match Winner</td>
                <td className="px-4 py-3">IND</td>
                <td className="px-4 py-3">200 coins</td>
                <td className="px-4 py-3">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                    Won
                  </span>
                </td>
              </tr>
              <tr className="bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700">
                <td className="px-4 py-3 font-medium">ENG vs NZ</td>
                <td className="px-4 py-3 text-gray-500">Jun 12, 2023</td>
                <td className="px-4 py-3">Score Range</td>
                <td className="px-4 py-3">140-160</td>
                <td className="px-4 py-3">100 coins</td>
                <td className="px-4 py-3">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
                    Lost
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        
        <div className="mt-4 flex justify-center">
          <button className="text-blue-600 dark:text-blue-400 hover:underline text-sm">
            View All Predictions
          </button>
        </div>
      </div>}
      */}
      <PredictionHistory />
    </div>
  );
};

// Rewards page removed

// Import the Referrals component
const ReferralsPage = () => <Referrals />;

// Add Dashboard redirect component
const DashboardRedirect = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const redirectUser = async () => {
      if (!currentUser) {
        navigate('/login');
        return;
      }
      
      try {
        const userRole = await fetchUserRole(currentUser.uid);
        const redirectPath = getRedirectPathByRole(userRole);
        navigate(redirectPath, { replace: true });
      } catch (error) {
        console.error("Error redirecting from dashboard:", error);
        // Default to user home if there's an error
        navigate('/user/home', { replace: true });
      } finally {
        setLoading(false);
      }
    };
    
    redirectUser();
  }, [currentUser, navigate]);
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }
  
  return null;
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/auth" element={<AuthPage />} />
      <Route path="/finishSignIn" element={<FinishSignIn />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/admin-matches" element={<AdminMatches />} />
      <Route path="/dashboard" element={<DashboardRedirect />} />
      <Route path="/admin-login" element={<AdminLogin />} />
      
      <Route element={<Layout />}>
        <Route path="/" element={<Home />} />
        <Route path="/matches" element={<Matches />} />
        <Route path="/matches/:matchId" element={<MatchDetailsPage />} />
        <Route path="/wallet" element={<Wallet />} />
        <Route path="/earn" element={<Earn />} />
        <Route path="/redeem" element={<Redeem />} />
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/referrals" element={<ReferralsPage />} />
        <Route path="/prediction-rules" element={<PredictionRules />} />
        <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/support" element={<Support />} />
        <Route path="/contact" element={<Contact />} />
        
        {/* User home page */}
        <Route path="/user/home" element={<UserHome />} />
        
        {/* Admin routes protected by AdminRouteGuard */}
        <Route element={<AdminRouteGuard />}>
          <Route path="/admin" element={<AdminPanel />} />
          <Route path="/admin/dashboard" element={<AdminDashboard />} />
          <Route path="/admin/support" element={<AdminDashboard />} />
          <Route path="/admin/users" element={<AdminPanel />} />
          <Route path="/admin/matches" element={<AdminPanel />} />
          <Route path="/admin/withdrawals" element={<AdminPanel />} />
          <Route path="/admin/stats" element={<AdminPanel />} />
          <Route path="/admin/dailycode" element={<AdminPanel />} />
          <Route path="/admin/blog-rewards" element={<AdminBlogRewards />} />
          <Route path="/admin/test-code" element={<TestCode />} />
        </Route>
      </Route>
    </Routes>
  );
}

/*
 * Prediction Logic Flow:
 *
 * 1. User selects a match and prediction type:
 *    - Match Winner (2x payout)
 *    - Team Score Range (3x payout)
 *    - Over-by-Over predictions (1.5x payout)
 *
 * 2. User selects prediction amount from Wallet A
 *
 * 3. On submission:
 *    - Amount is deducted from Wallet A
 *    - Prediction is saved to Firestore (predictions/{matchId}/{uid})
 *    - User sees prediction in "Your Predictions" section
 *
 * 4. When match result is determined:
 *    - Admin updates match results
 *    - System evaluates all predictions
 *    - For winning predictions:
 *      - Wallet B is credited with winnings
 *      - Prediction status is updated (Won/Lost)
 */

function App() {
  return (
    <Router>
      <ScrollToTop />
      <AuthProvider>
        <WalletProvider>
          <ReferralProvider>
            <PredictionProvider>
              <Toaster position="top-right" />
              <AppRoutes />
              <LoginRewardToast />
            </PredictionProvider>
          </ReferralProvider>
        </WalletProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
